import { useState } from 'react';
import { ShoppingCart, Check, TrendingDown, Images } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { getImageUrl, getTypeLabel } from '../api/plvApi';
import type { PLVProduct } from '../types/plv.types';
import { calculateInitialQuantity, generateQuantityOptions, formatQuantity } from '../utils/quantityUtils';
import { calculateTotalPriceForProduct, formatPrice, getPricingInfo } from '../utils/pricingUtils';
import { PricingTiersModal } from './PricingTiersModal';

interface ProductCardProps {
  product: PLVProduct;
  onImageClick: () => void;
}

export function ProductCard({ product, onImageClick }: ProductCardProps) {
  const { addItem, getItemQuantity } = useCart();
  const initialQuantity = calculateInitialQuantity(product);
  const quantityOptions = generateQuantityOptions(product, 10);

  const [quantity, setQuantity] = useState(initialQuantity);
  const [showPricingModal, setShowPricingModal] = useState(false);

  const currentQuantityInCart = getItemQuantity(product.id.toString());
  const mainImage = product.files?.presentationImage || product.images?.[0];
  const imageCount =
    (product.files?.printFiles?.length || 0) +
    (product.files?.presentationImage ? 1 : 0) ||
    product.images?.length ||
    0;
  const hasMultipleImages = imageCount > 1;

  const handleAddToCart = () => {
    addItem(product, quantity);
    setQuantity(initialQuantity);
  };

  const pricingInfo = getPricingInfo(product);
  const totalPrice = calculateTotalPriceForProduct(product, quantity);

  return (
    <>
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
        {/* Image */}
        <div className="tw-relative tw-cursor-pointer" onClick={onImageClick}>
          <div className="tw-h-48 tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
            {mainImage ? (
              <img
                src={mainImage.startsWith('http') ? mainImage : getImageUrl(mainImage)}
                alt={product.name}
                className="tw-max-h-full tw-max-w-full tw-object-contain"
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="tw-text-center tw-text-gray-400">
                <div className="tw-text-4xl tw-mb-1">📦</div>
                <span className="tw-text-sm">Aucune image</span>
              </div>
            )}
          </div>
          {hasMultipleImages && (
            <span className="tw-absolute tw-top-2 tw-right-2 tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded tw-bg-black/60 tw-text-white">
              <Images className="tw-w-3.5 tw-h-3.5" />
              {imageCount}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="tw-p-4 tw-flex tw-flex-col tw-flex-1">
          <div className="tw-flex-1">
            <div className="tw-flex tw-justify-between tw-items-start tw-gap-2 tw-mb-2">
              <h6 className="tw-text-sm tw-font-semibold tw-leading-tight">{product.name}</h6>
              <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded tw-bg-gray-100 tw-text-gray-600 tw-flex-shrink-0">
                {getTypeLabel(product.type)}
              </span>
            </div>
            {product.description && (
              <p className="tw-text-xs tw-text-gray-500 tw-mb-3 tw-whitespace-pre-wrap">
                {product.description.length > 100
                  ? `${product.description.substring(0, 100)}...`
                  : product.description}
              </p>
            )}
          </div>

          {/* Price & Actions */}
          <div className="tw-mt-auto">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
              <span className="tw-font-bold tw-text-sm">
                {formatPrice(totalPrice)}{' '}
                <span className="tw-text-xs tw-text-gray-400 tw-font-normal">HT</span>
              </span>
              {pricingInfo.hasMultiplePairs && (
                <button
                  className="tw-text-xs tw-text-gray-500 hover:tw-text-affinicar-secondary tw-transition-colors tw-flex tw-items-center tw-gap-1"
                  onClick={() => setShowPricingModal(true)}
                >
                  <TrendingDown className="tw-w-3.5 tw-h-3.5" />
                  Prix dégressifs
                </button>
              )}
            </div>

            <div className="tw-flex tw-gap-2">
              <select
                className="tw-w-24 tw-rounded-lg tw-border tw-border-gray-300 tw-px-2 tw-py-1.5 tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-affinicar-secondary focus:tw-border-affinicar-secondary"
                value={quantity}
                onChange={e => setQuantity(Number(e.target.value))}
              >
                {quantityOptions.map(option => (
                  <option key={option} value={option}>
                    {formatQuantity(option)}
                  </option>
                ))}
              </select>
              <button
                className="tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-affinicar-secondary tw-text-white hover:tw-opacity-90 tw-transition-opacity"
                onClick={handleAddToCart}
              >
                <ShoppingCart className="tw-w-4 tw-h-4" />
                Ajouter
              </button>
            </div>

            {currentQuantityInCart > 0 && (
              <p className="tw-mt-2 tw-text-xs tw-text-green-600 tw-flex tw-items-center tw-gap-1">
                <Check className="tw-w-3.5 tw-h-3.5" />
                {currentQuantityInCart} dans le panier
              </p>
            )}
          </div>
        </div>
      </div>

      {showPricingModal && (
        <PricingTiersModal product={product} onClose={() => setShowPricingModal(false)} />
      )}
    </>
  );
}
